import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Check, X } from "lucide-react";

const rows = [
  {
    label: "Estrategia de contenido",
    without: "Improvisas cada semana, sin plan",
    with: "Calendario editorial trimestral con temas y objetivos",
  },
  {
    label: "Producción",
    without: "Vídeos que parecen una videollamada de Zoom",
    with: "Setup multicámara 4K con iluminación y sonido profesional",
  },
  {
    label: "Tu tiempo invertido",
    without: "10-15 horas por episodio (grabar, editar, publicar)",
    with: "Solo 60-90 min: llegas, grabas y te vas. Nosotros hacemos el resto",
  },
  {
    label: "Postproducción",
    without: "Edición amateur, sin ritmo, sin gancho",
    with: "Edición profesional + clips optimizados por red social",
  },
  {
    label: "Distribución",
    without: "Subes a YouTube y ya. Sin más recorrido",
    with: "10+ piezas de contenido derivadas: Shorts, Reels, LinkedIn, newsletter",
  },
  {
    label: "Resultados a 6 meses",
    without: "Contenido esporádico sin retorno claro",
    with: "Autoridad de marca + leads cualificados + activo evergreen",
  },
];

export default function Comparison() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="section-spacing bg-gradient-to-br from-slate-50 to-blue-50" data-testid="comparison-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-galvani-pink font-body font-semibold mb-4">
            Con Galvani vs Sin Galvani
          </span>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-galvani-text mb-4">
            La diferencia entre <span className="gradient-text">grabar</span> y <span className="gradient-text">producir</span>
          </h2>
          <p className="font-body text-galvani-textLight max-w-2xl mx-auto">
            Comparativa honesta de lo que cambia cuando profesionalizas tu podcast corporativo.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden"
        >
          {/* Table header */}
          <div className="grid grid-cols-1 md:grid-cols-3 border-b border-slate-200">
            <div className="hidden md:block p-6 bg-slate-50" />
            <div className="p-6 border-t md:border-t-0 md:border-l border-slate-200 bg-red-50">
              <div className="flex items-center gap-2 text-red-600 font-heading font-bold">
                <X className="w-5 h-5" />
                Sin Galvani
              </div>
            </div>
            <div className="p-6 border-t md:border-t-0 md:border-l border-slate-200 bg-gradient-to-br from-blue-50 to-purple-50">
              <div className="flex items-center gap-2 text-galvani-purple font-heading font-bold">
                <Check className="w-5 h-5" />
                Con Galvani
              </div>
            </div>
          </div>

          {/* Table rows */}
          {rows.map((row, idx) => (
            <motion.div
              key={row.label}
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.1 + idx * 0.06 }}
              className={`grid grid-cols-1 md:grid-cols-3 ${idx < rows.length - 1 ? "border-b border-slate-100" : ""}`}
            >
              <div className="p-5 md:p-6 bg-slate-50 md:bg-transparent">
                <h3 className="font-heading font-semibold text-galvani-text text-sm md:text-base">
                  {row.label}
                </h3>
              </div>
              <div className="p-5 md:p-6 md:border-l border-slate-100">
                <div className="flex items-start gap-3">
                  <X className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
                  <p className="font-body text-sm text-galvani-textLight leading-relaxed">
                    {row.without}
                  </p>
                </div>
              </div>
              <div className="p-5 md:p-6 md:border-l border-slate-100 bg-gradient-to-br from-transparent to-purple-50/40">
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                  <p className="font-body text-sm text-galvani-text leading-relaxed">
                    {row.with}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
